import { ModalBaseComponent } from '@/components/base/modal-base/modal-base.component';
import { DocAddWidgetComponent } from '@/components/doc-add-widget/doc-add-widget.component';
import { InputComponent } from '@/components/ui-forms/input/input.component';
import { SelectComponent } from '@/components/ui-forms/select/select.component';
import { NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
  DocumentSummary,
  EnergyClassification,
  PropertiesBookletService,
  PropertyBookletEquipmentDetail,
  PropertyBookletEquipmentMaintenance,
  PropertyBookletEquipmentNetworkDetail,
  PropertyBookletEquipments,
  PropertyBookletType,
  RealProperty
} from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { PropertyBookletEquipmentType } from '../property-booklet-equipment-card/property-booklet-equipment-card.component';
import { PropertyBookletEquipmentService } from '../property-booklet-equipment-card/property-booklet-equipment.service';

export class PropertyBookletEquipmentDialogData {
  prop: RealProperty;
  bookletUid: string;
  bookletType: PropertyBookletType;
  type: PropertyBookletEquipmentType;
}

@Component({
  selector: 'app-property-booklet-equipment-dialog',
  standalone: true,
  imports: [
    ModalBaseComponent,
    NgIf,
    NgTemplateOutlet,
    MatDialogModule,
    ReactiveFormsModule,
    DocAddWidgetComponent,
    InputComponent,
    SelectComponent,
    MatFormFieldModule
  ],
  templateUrl: './property-booklet-equipment-dialog.component.html',
  styleUrl: './property-booklet-equipment-dialog.component.css'
})
export class PropertyBookletEquipmentDialogComponent {

  private readonly propertiesBookletService = inject(PropertiesBookletService);

  bookletUid: string;
  isBookletRenovation = false;
  prop: RealProperty;
  sub = new Subscription();
  arePopulateData = false;

  title: string;
  type: PropertyBookletEquipmentType
  isLoading = false;
  PropertyBookletEquipmentType = PropertyBookletEquipmentType;
  energyClassificationList: EnergyClassification[] = Object.values(EnergyClassification);
  maintenanceTypeList: number[] = [1, 2, 3, 4];


  get form_installationDate() {
    return this.form.get('installationDate') as FormControl<string>;
  }
  get form_energyClassification() {
    return this.form.get('energyClassification') as FormControl<EnergyClassification>;
  }
  get form_installationDetails() {
    return this.form.get('installationDetails') as FormControl<DocumentSummary>;
  }
  get form_productSheet() {
    return this.form.get('productSheet') as FormControl<DocumentSummary>;
  }
  get form_operationManual() {
    return this.form.get('operationManual') as FormControl<DocumentSummary>;
  }
  // maintenance
  get form_maintenanceDate() {
    return this.form.get('maintenanceDate') as FormControl<string>;
  }
  get form_maintenanceType() {
    return this.form.get('maintenanceType') as FormControl<number>;
  }
  get form_maintenanceCertificate() {
    return this.form.get('maintenanceCertificate') as FormControl<DocumentSummary>;
  }
  // network
  get form_subStation() {
    return this.form.get('subStation') as FormControl<string>;
  }

  form = this.fb.group({
    installationDate: [null],
    energyClassification: [null],
    productSheet: [null],
    installationDetails: [null],
    operationManual: [null],
    // maintenance
    maintenanceDate: [null],
    maintenanceType: [null],
    maintenanceCertificate: [null],
    // network
    subStation: [null],

  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PropertyBookletEquipmentDialogData,
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<PropertyBookletEquipmentDialogComponent>,
  ) {
    this.prop = data.prop;
    this.bookletUid = data.bookletUid;
    this.isBookletRenovation = data.bookletType===PropertyBookletType.RENOVATION;
    this.type = data.type;
    this.title = PropertyBookletEquipmentService.getTitle(data.type);

    this.initForm();
  }

  initForm() {
    this.populateData();
    if(this.isBookletRenovation){
      this.form_installationDate.addValidators(Validators.required);
    }
  }

  setValidators() {
    if (this.type === PropertyBookletEquipmentType.heatingMaintenance) {
      this.form_maintenanceDate.addValidators(Validators.required);
      this.form_maintenanceType.addValidators(Validators.required);
      this.form_maintenanceCertificate.addValidators(Validators.required);
    } else if (this.type === PropertyBookletEquipmentType.network) {
      this.form_installationDetails.addValidators(Validators.required);
      this.form_subStation.addValidators(Validators.required);
    } else {
      this.form_energyClassification.addValidators(Validators.required);
      this.form_productSheet.addValidators(Validators.required);
    }
  }

  populateData() {
    this.isLoading = true;

    this.sub = this.propertiesBookletService.getRealPropertyBookletEquipments(this.prop.uid, this.bookletUid).subscribe({
      next: (resp) => {
        this.isLoading = false;
        this.setFormFromData(resp);
      },
      error: () => {
        this.isLoading = false;
      },
      complete: () => {
        this.arePopulateData = true;
      }
    });
  }

  setFormFromData(resp: PropertyBookletEquipments) {
    this.setValidators();
    const EQUIPMENT_KEY: keyof PropertyBookletEquipmentDetail = 'energyClassification';
    const MAINTENANCE_KEY: keyof PropertyBookletEquipmentMaintenance = 'maintenanceDate';
    const NETWORK_KEY: keyof PropertyBookletEquipmentNetworkDetail = 'subStation';

    const key = resp?.[this.type] ?? null;
    if (!key) {
      this.form_installationDate.setValue(null);
      this.form_productSheet.setValue(null);
      this.form_energyClassification.setValue(null);
      this.form_installationDetails.setValue(null);
      this.form_operationManual.setValue(null);
      this.form_maintenanceDate.setValue(null);
      this.form_maintenanceType.setValue(null);
      this.form_maintenanceCertificate.setValue(null);
      this.form_subStation.setValue(null);
      return;
    }

    if (EQUIPMENT_KEY in key) {
      this.form_installationDate.setValue(key?.installationDate ?? null);
      this.form_productSheet.setValue(key.productSheet ?? null);
      this.form_energyClassification.setValue(key?.energyClassification ?? null);
      this.form_installationDetails.setValue(key.installationDetails ?? null);
      this.form_operationManual.setValue(key.operationManual ?? null);
    } else if(MAINTENANCE_KEY in key) {
      this.form_maintenanceDate.setValue(key?.maintenanceDate ?? null);
      this.form_maintenanceType.setValue(key.maintenanceType ?? null);
      this.form_maintenanceCertificate.setValue(key?.maintenanceCertificate ?? null);
    } else if(NETWORK_KEY in key) {
      this.form_installationDate.setValue(key?.installationDate ?? null);
      this.form_installationDetails.setValue(key.installationDetails ?? null);
      this.form_subStation.setValue(key?.subStation ?? null);
    }

  }

  onProductSheetDocSelected(newDoc: DocumentSummary) {
    this.form_productSheet.setValue(newDoc)
  }

  onProductSheetDocDeleted() {
    this.form_productSheet.setValue(null);
  }

  onInstallationDetailsDocSelected(newDoc: DocumentSummary) {
    this.form_installationDetails.setValue(newDoc)
  }

  onInstallationDetailsDocDeleted() {
    this.form_installationDetails.setValue(null);
  }

  onOperationManualDocSelected(newDoc: DocumentSummary) {
    this.form_operationManual.setValue(newDoc)
  }

  onOperationManualDocDeleted() {
    this.form_operationManual.setValue(null);
  }

  onMaintenanceCertificateDocSelected(newDoc: DocumentSummary) {
    this.form_maintenanceCertificate.setValue(newDoc)
  }

  onMaintenanceCertificateDocDeleted() {
    this.form_maintenanceCertificate.setValue(null);
  }

  onSubmit() {
    if (this.form.valid) {
      this.update();
    }
  }

  update() {
    this.isLoading = true;
    const body = this.getBody();
    this.sub = this.propertiesBookletService.updateRealPropertyBookletEquipments(this.prop.uid, this.bookletUid, body).subscribe({
      next: () => {
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isLoading = false;
      },
    });
  }
  getBody(): PropertyBookletEquipments {
    const body: PropertyBookletEquipments = {};
    if (this.type == PropertyBookletEquipmentType.heatingMaintenance) {
      body[this.type] = {
        maintenanceDate: this.form_maintenanceDate.value,
        maintenanceCertificate: this.form_maintenanceCertificate.value,
        maintenanceType: this.form_maintenanceType.value,
      }
    } else if (this.type == PropertyBookletEquipmentType.network) {
      body[this.type] = {
        installationDate: this.form_installationDate.value,
        installationDetails: this.form_installationDetails.value,
        subStation: this.form_subStation.value,
      }
    } else {
      body[this.type] = {
        installationDate: this.form_installationDate.value,
        energyClassification: this.form_energyClassification.value,
        productSheet: this.form_productSheet.value,
        installationDetails: this.form_installationDetails.value,
        operationManual: this.form_operationManual.value,
      }
    }

    return body;
  }
}
